import Header from "@/components/Header";
import { prefix } from ".";
import Footer from "@/components/Footer";
import { useDispatch, useSelector } from "react-redux";
import { IReducersState } from "@/reducers";
import Business from "@/components/Business";
import { useTranslation } from "react-i18next";
import { ReactNode, useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getOrderDetailsByEndpoint } from "@/interface/order";
import { IObject } from "@/types/common-types";
import dayjs from "dayjs";
import { formatCurrency, plus } from "@/utils";
import { IDispatch } from "@/actions";
import ActionType from "@/actions/action-type";

interface ILineDisplayProps {
  name: string;
  value: ReactNode;
  className?: string;
}

const LineDisplay: React.FC<ILineDisplayProps> = (props) => {
  const { name, value, className = '' } = props;
  return (
    <div className={`${prefix}-result-item ${className}`}>
      <div className={`${prefix}-result-item-label`}>
        {name}:
      </div>
      <div className={`${prefix}-result-item-value`}>
        {value}
      </div>
    </div>
  )
}

const OrderResultPage = () => {
  const { businessID = '' } = useParams();
  const [searchParams] = useSearchParams();
  const refNo = searchParams.get('refNo') || '';
  const { t } = useTranslation('common');
  const dispatch: IDispatch = useDispatch();
  const common = useSelector((state: IReducersState) => state.common);
  const { business } = common;
  const { storeID: businessStoreID } = business;

  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<IObject>();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasClearedCartRef = useRef(false);

  useEffect(() => {
    loadOrderResult(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refNo || !businessStoreID || hasClearedCartRef.current) {
      return;
    }

    dispatch({
      type: ActionType.CLEAR_SHOPPING_CART,
      storeID: businessStoreID,
    });
    hasClearedCartRef.current = true;
  }, [refNo, businessStoreID, dispatch]);

  useEffect(() => {
    if (orderDetails && shouldStopPolling(orderDetails)) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [orderDetails]);

  useEffect(() => {
    if (!refNo || !businessID) {
      return;
    }

    if (orderDetails && shouldStopPolling(orderDetails)) {
      return;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      loadOrderResult(false);
    }, 10000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refNo, businessID]);

  // 检查是否应该停止轮询
  const shouldStopPolling = (order: IObject) => {
    const { correspondence_state: status, pickup_status: pickupStatus } = order;
    
    if (pickupStatus === 3) {
      return true;
    }
    
    if (status === 'voided' || status === 'refunded') {
      return true;
    }
    
    return false;
  };

  const loadOrderResult = async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    }

    try {
      const orderDetails = await getOrderDetailsByEndpoint(refNo, businessID);

      if (orderDetails && orderDetails.order) {
        const order = orderDetails.order;
        setOrderDetails(order);
        
        if (shouldStopPolling(order) && pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.log('error', error);
    }

    if (isInitial) {
      setLoading(false);
    }
  }

  const getStatus = (orderDetails: IObject) => {
    const { correspondence_state: status, pickup_status: pickupStatus } = orderDetails;

    let showStatus = '';
    let showStatusClass = '';
    if (status === 'unpaid') {
      showStatus = t('orderStatusPending');
      showStatusClass = 'unpaid';
    } else if (status === 'refunded') {
      showStatus = t('orderStatusRefunded');
      showStatusClass = 'refunded';
    } else if (status === 'voided') {
      showStatus = t('orderStatusCancelled');
      showStatusClass = 'voided';
    } else {
      if (pickupStatus === 0) {
        showStatus = t('orderStatusPendingAccept');
        showStatusClass = 'pending_accept';
      }

      if (pickupStatus === 1) {
        showStatus = t('orderStatusAccepted');
        showStatusClass = 'accepted';
      }

      if (pickupStatus === 3) {
        showStatus = t('orderStatusCompleted');
        showStatusClass = 'completed';
      }
    }

    return <span className={`${prefix}-result-item-status ${showStatusClass}`}>{showStatus}</span>;
  };

  let totalQuanity = 0;
  Object.values(orderDetails?.line_items || {}).forEach((item: any) => {
    totalQuanity = plus(totalQuanity, item.quantity || 0);
  });

  return (
    <>
      <div className={`${prefix}`}>
        <Header />

        <div className={`${prefix}-content`}>
          <Business
            t={t}
            loading={loading}
            business={business}
          />
          <div className={`${prefix}-result`}>
            <LineDisplay
              name={t('status')}
              className={'status'}
              value={getStatus(orderDetails || {})}
            />
            <LineDisplay
              name={t('orderNumber')}
              value={refNo}
            />
            <LineDisplay
              name={t('orderTime')}
              value={dayjs(orderDetails?.created_at).format('YYYY-MM-DD HH:mm:ss')}
            />

            <div className={`${prefix}-result-collapsible`}>
              <div className={`${prefix}-result-collapsible-header`}>
                <div className={`${prefix}-result-collapsible-title`}>
                  {t('orderedItems')}
                </div>
                <div
                  className={`${prefix}-result-collapsible-button`}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? t('collapse') : t('expand')}
                </div>
              </div>
              <div 
                className={`${prefix}-result-collapsible-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}
              >
                <div className={`${prefix}-result-collapsible-content`}>
                  {orderDetails?.line_items?.map((item: any) => {
                    const imageUrl = item.image_url || '';
                    const name = item.label || '';
                    const modifier = '';
                    const price = item.price || 0;
                    const quantity = item.quantity || 1;

                    return (
                      <div className={`${prefix}-result-collapsible-content-item`} key={item.id || item.uuid}>
                        <div className={`${prefix}-result-collapsible-content-item-left`}>
                          {imageUrl ? (
                            <img
                              className={`${prefix}-result-collapsible-content-item-image`}
                              src={imageUrl}
                              alt={name}
                            />
                          ) : (
                            <div className={`${prefix}-result-collapsible-content-item-default`}>
                              <img
                                className={`${prefix}-result-collapsible-content-item-default-img`}
                                src="/images/default-product.png"
                                alt=""
                              />
                            </div>
                          )}
                          <div className={`${prefix}-result-collapsible-content-item-info`}>
                            <div className={`${prefix}-result-collapsible-content-item-name`}>
                              {name}
                            </div>
                            {modifier && (
                              <div className={`${prefix}-result-collapsible-content-item-modifier`}>
                                {modifier}
                              </div>
                            )}
                            <div className={`${prefix}-result-collapsible-content-item-price`}>
                              {formatCurrency(Number(price))}
                            </div>
                          </div>
                        </div>
                        <div className={`${prefix}-result-collapsible-content-item-quantity`}>
                          x{quantity}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={`${prefix}-result-item total`}>
              <div className={`${prefix}-result-item-label`}>
                {t('total')}: {totalQuanity}
              </div>
              <div className={`${prefix}-result-item-value total`}>
                {formatCurrency(Number(orderDetails?.initial_total || 0))}
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  )
}

export default OrderResultPage;